import React, { useState, useEffect, useCallback, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { fetchPOData } from "./hooks";
import {
  groupInvoiceItemsByNumber,
  formatNumber,
  normalizePrice,
  normalizeItemNumber,
  sumPrices,
  getItemStatus,
  PASS,
  SPECIAL,
  FAIL,
  NOT_FOUND,
} from "./utils";
import Button from "components/base/Button";
import {
  FiExternalLink,
  FiRefreshCw,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi";

// List of special items for AB invoices that should be ignored in PO validation
const SPECIAL_ITEMS = {
  AB: [
    "Distribution mark label",
    "Labor cost of distribution mark label",
    "Barcode stickers",
  ],
  LTD: [],
};

const MAX_PO_FETCH_CONCURRENCY = 1;

const Status = ({ status }) => {
  switch (status) {
    case PASS:
      return <span className="text-xs font-semibold text-green-600">Pass</span>;
    case SPECIAL:
      return <span className="text-xs font-semibold text-gray-400">-</span>;

    case FAIL:
      return <span className="text-xs font-semibold text-red-600">Fail</span>;
    case NOT_FOUND:
      return (
        <span className="text-xs font-semibold text-red-600">Not in Odoo</span>
      );

    default:
      return <span className="text-xs font-semibold text-red-600">Error</span>;
  }
};

const Row = ({ index, summaryItem, detailItems, odooItems, status }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => setIsExpanded((prev) => !prev);

  const isWrong = status !== PASS && status !== SPECIAL;
  const multiPirce = summaryItem.hasMultipleUnitPrice;
  const isMultipleItems = detailItems.length > 1;

  // get Odoo item data for this row
  const odooItem =
    summaryItem.itemNumber && odooItems
      ? odooItems[normalizeItemNumber(summaryItem.itemNumber)]
      : null;

  const rowClassName = `font-medium border-b border-gray-100 
    ${multiPirce ? "bg-red-50" : ""} 
    ${isWrong ? "text-red-600" : ""} 
    ${isMultipleItems ? "cursor-pointer hover:bg-gray-50" : ""}`;

  const displayProductSpec = () => {
    if (!isMultipleItems) return detailItems[0].productSpec;

    const firstSpec = detailItems[0].productSpec || "";
    return firstSpec.length > 20
      ? `${firstSpec.substring(0, 20)}...`
      : firstSpec;
  };

  const showOdooData = status === FAIL && !multiPirce && odooItem;

  return (
    <>
      <tr
        className={rowClassName}
        onClick={isMultipleItems ? toggleExpand : undefined}
      >
        <td className="py-2 px-3 text-center">{index}</td>
        <td className="py-2 px-3">
          <div className="flex items-center">
            <span>{summaryItem.itemNumber}</span>

            {isMultipleItems && (
              <Button
                className="flex items-center ml-2 px-2 bg-blue-50 rounded-full text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors h-7"
                icon={
                  isExpanded ? (
                    <FiChevronDown size={14} />
                  ) : (
                    <FiChevronRight size={14} />
                  )
                }
              >
                <span className="text-xs mr-1">{detailItems.length} items</span>
              </Button>
            )}
          </div>
        </td>
        <td className="py-2 px-3">
          <span>{displayProductSpec()}</span>
          {status === FAIL && odooItem && (
            <div className="text-xs font-medium mt-1 text-fuchsia-800">
              Odoo Item --{">"}
            </div>
          )}
        </td>
        <td className="py-2 px-3 text-center">
          <span>{summaryItem.quantity}</span>
          {showOdooData && (
            <div className="text-xs font-medium mt-1 text-fuchsia-800">
              {odooItem.quantity}
            </div>
          )}
        </td>
        <td className="py-2 px-3 text-center">
          {multiPirce ? (
            <span className="text-red-600">Multiple Price</span>
          ) : (
            <>
              <span>{formatNumber(summaryItem.unitPrice)}</span>
              {showOdooData && (
                <div className="text-xs font-medium mt-1 text-fuchsia-800">
                  {odooItem.unitPrice}
                </div>
              )}
            </>
          )}
        </td>
        <td className="py-2 px-3 text-center">
          <>
            <span>{formatNumber(summaryItem.totalPrice, 2)}</span>
            {showOdooData && (
              <div className="text-xs font-medium mt-1 text-fuchsia-800">
                {odooItem.totalPrice}
              </div>
            )}
          </>
        </td>
        <td className="py-2 px-3 text-center">
          <Status status={status} />
        </td>
      </tr>

      {isMultipleItems &&
        isExpanded &&
        detailItems.map((item, idx) => {
          return (
            <tr
              className="border-b border-gray-100 text-gray-500 bg-gray-50"
              key={idx}
            >
              <td className="py-2 px-3"></td>
              <td className="py-2 px-3"></td>
              <td className="py-2 px-3">{item.productSpec}</td>
              <td className="py-2 px-3 text-center">{item.quantity}</td>
              <td className="py-2 px-3 text-center">
                {formatNumber(item.unitPrice)}
              </td>
              <td className="py-2 px-3 text-center font-medium">
                {formatNumber(item.totalPrice, 2)}
              </td>
              <td className="py-2 px-3 text-center"></td>
            </tr>
          );
        })}
    </>
  );
};

const POLinkButton = ({ poId, invoiceType }) => {
  const openPOLink = () => {
    if (poId) {
      const odooHost = process.env.REACT_APP_ODOO_HOST || "waboba.odoo.com";
      const cids = invoiceType === "LTD" ? "3" : "1";
      const url = `https://${odooHost}/web#id=${poId}&cids=${cids}&menu_id=296&action=441&model=purchase.order&view_type=form`;
      window.open(url, "_blank");
    }
  };

  if (!poId) return null;

  return (
    <Button
      onClick={openPOLink}
      className="h-7 px-3 text-xs bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 rounded-full"
      icon={<FiExternalLink size={14} />}
    >
      View
    </Button>
  );
};

const RefreshButton = ({ onRefresh, isLoading }) => {
  return (
    <Button
      onClick={onRefresh}
      loading={isLoading}
      disabled={isLoading}
      className="h-7 px-3 text-xs bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 rounded-full"
      icon={<FiRefreshCw size={14} />}
    >
      Refresh
    </Button>
  );
};

const POCard = ({
  poName,
  invoiceItems,
  totalPrice,
  invoiceType = "LTD",
  fetchState,
  onRefresh = () => {},
}) => {
  const fetchStatus = fetchState?.status || "idle";
  const isQueued = fetchStatus === "queued";
  const isLoading = fetchStatus === "idle" || fetchStatus === "loading";
  const error = fetchState?.error || null;
  const data = fetchState?.data || null;

  const poId = data?.poId;
  const odooItems = data?.poItemsData;
  const odooTotalPrice = data?.odooTotalPrice;
  const hasOdooTotalPrice =
    odooTotalPrice !== "" &&
    odooTotalPrice !== null &&
    odooTotalPrice !== undefined;
  const totalPricesMatch =
    hasOdooTotalPrice &&
    normalizePrice(totalPrice, 2) === normalizePrice(odooTotalPrice, 2);
  const isPOInvalid = error || (!isLoading && !poId);

  const groupedByItemNumber = groupInvoiceItemsByNumber(invoiceItems);

  // get the status of each row and overall PO status
  const rowStatuses = {};
  let poStatus = null;

  if (odooItems && invoiceItems.length > 0) {
    // get the status of each row
    Object.keys(groupedByItemNumber).forEach((itemNumber) => {
      const group = groupedByItemNumber[itemNumber];
      rowStatuses[itemNumber] = getItemStatus(
        group.summary,
        odooItems,
        invoiceType,
        SPECIAL_ITEMS,
      );
    });

    // get the overall status
    const allStatuses = Object.values(rowStatuses);
    const isPassed = allStatuses.every(
      (status) => status === PASS || status === SPECIAL,
    );
    poStatus = isPassed ? PASS : FAIL;
  }

  return (
    <div className="card space-y-4">
      {/* Card Header */}
      <div className="flex items-center">
        {/* PO Name */}
        {isPOInvalid ? (
          <span className="line-through text-red-600">{poName}</span>
        ) : (
          <>
            <h2>{poName}</h2>
            {invoiceItems.length > 0 && (
              <span
                className={`ml-3 px-2 py-1 rounded text-sm font-bold ${
                  poStatus === PASS
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {poStatus === PASS ? "PASS" : "FAIL"}
              </span>
            )}
          </>
        )}

        {/* Buttons */}
        <div className="flex items-center ml-6 space-x-3">
          <POLinkButton poId={poId} invoiceType={invoiceType} />

          <RefreshButton
            onRefresh={() => {
              if (!isLoading) {
                onRefresh();
              }
            }}
            isLoading={isLoading}
          />
        </div>

        <div className="ml-auto flex items-center gap-2 text-sm whitespace-nowrap">
          <span className="font-medium text-black">
            Excel Total {formatNumber(totalPrice, 2)}
          </span>
          {hasOdooTotalPrice && (
            <>
              <span
                className={`font-bold ${
                  totalPricesMatch ? "text-green-600" : "text-red-600"
                }`}
              >
                {totalPricesMatch ? "=" : "≠"}
              </span>
              <span className="font-medium text-fuchsia-800">
                Odoo Total {formatNumber(odooTotalPrice, 2)}
              </span>
            </>
          )}
        </div>
      </div>

      {/* When loading or Failed */}
      {isQueued ? (
        <div className="p-4 text-center text-gray-500">
          Queued for Odoo query...
        </div>
      ) : isLoading ? (
        <div className="p-4 text-center text-gray-500">Loading Odoo...</div>
      ) : !poId ? (
        <div className="p-4 text-center text-red-500">
          {error || `PO "${poName}" was not found in Odoo.`}
        </div>
      ) : null}

      {/* Content */}
      {poId && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <colgroup>
              <col className="w-[6%]" />
              <col className="w-[14%]" />
              <col className="w-[32%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[14%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead className="bg-gray-100 text-sm text-gray-600">
              <tr>
                <th className="py-2 px-3 text-center">#</th>
                <th className="py-2 px-3 text-left whitespace-nowrap">
                  Item Number
                </th>
                <th className="py-2 px-3 text-left">Product Spec</th>
                <th className="py-2 px-3 text-center whitespace-nowrap">
                  Quantity
                </th>
                <th className="py-2 px-3 text-center whitespace-nowrap">
                  Unit Price
                </th>
                <th className="py-2 px-3 text-center whitespace-nowrap">
                  Total Price
                </th>
                <th className="py-2 px-3 text-center whitespace-nowrap">
                  Status
                </th>
              </tr>
            </thead>

            <tbody>
              {Object.keys(groupedByItemNumber).map((itemNumber, index) => (
                <Row
                  key={itemNumber}
                  index={index + 1}
                  status={rowStatuses[itemNumber]}
                  summaryItem={groupedByItemNumber[itemNumber].summary}
                  detailItems={groupedByItemNumber[itemNumber].items}
                  odooItems={odooItems}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const VerificationRules = () => (
  <div className="space-y-2 px-1 text-sm text-gray-600">
    <p>
      <span className="font-semibold text-gray-900">1. Excel source.</span> PO
      number, item details, and{" "}
      <span className="font-semibold text-gray-900">
        quantity, unit price, and Total Price
      </span>{" "}
      are read directly from the Excel file.
    </p>
    <p>
      <span className="font-semibold text-gray-900">2. Comparison.</span> A row
      is Pass only when its{" "}
      <span className="font-semibold text-gray-900">Item Number exists</span> in
      the Odoo PO and its{" "}
      <span className="font-semibold text-gray-900">
        grouped Quantity, Unit Price, and Total Price match
      </span>
      {". A PO is Pass only when every extracted Excel item is Pass."}
    </p>
  </div>
);

export const POList = ({ extractedData, systemType = "LTD" }) => {
  const [groupedByPO, setGroupedByPO] = useState({});
  const [poFetchMap, setPoFetchMap] = useState({});
  const client = useApolloClient();
  const poQueueRef = useRef([]);
  const inFlightRef = useRef(0);
  const generationRef = useRef(0);

  const updatePOFetchState = useCallback((poName, patch) => {
    setPoFetchMap((prevMap) => {
      const currentState = prevMap[poName];
      if (!currentState) {
        return prevMap;
      }

      return {
        ...prevMap,
        [poName]: {
          ...currentState,
          ...patch,
        },
      };
    });
  }, []);

  const processQueue = useCallback(
    (generation = generationRef.current) => {
      if (generation !== generationRef.current) {
        return;
      }

      while (
        inFlightRef.current < MAX_PO_FETCH_CONCURRENCY &&
        poQueueRef.current.length > 0
      ) {
        const poName = poQueueRef.current.shift();
        inFlightRef.current += 1;

        updatePOFetchState(poName, {
          status: "loading",
          error: null,
        });

        fetchPOData(client, poName)
          .then((result) => {
            if (generation !== generationRef.current) {
              return;
            }

            updatePOFetchState(poName, {
              status: "done",
              data: result.data,
              error: result.error,
            });
          })
          .finally(() => {
            if (generation !== generationRef.current) {
              return;
            }

            inFlightRef.current = Math.max(0, inFlightRef.current - 1);
            processQueue(generation);
          });
      }
    },
    [client, updatePOFetchState],
  );

  // Use useEffect to process initial data and avoid re-processing on each render
  useEffect(() => {
    const newGroupedByPO = {};

    // Group items by PO name and calculate total price for each PO
    extractedData.forEach((item) => {
      const poName = item.poName;

      if (!newGroupedByPO[poName]) {
        newGroupedByPO[poName] = { items: [], total: "0.00" };
      }

      newGroupedByPO[poName].items.push(item);

      newGroupedByPO[poName].total = sumPrices(
        [newGroupedByPO[poName].total, item.totalPrice],
        2,
      );
    });

    const poNames = Object.keys(newGroupedByPO);
    const initialFetchMap = {};
    poNames.forEach((poName) => {
      initialFetchMap[poName] = {
        status: "queued",
        data: null,
        error: null,
      };
    });

    setGroupedByPO(newGroupedByPO);
    setPoFetchMap(initialFetchMap);

    generationRef.current += 1;
    poQueueRef.current = [...poNames];
    inFlightRef.current = 0;
    processQueue(generationRef.current);
  }, [extractedData, processQueue]);

  const enqueuePOFetch = useCallback(
    (poName, prioritize = false) => {
      poQueueRef.current = poQueueRef.current.filter((name) => name !== poName);
      if (prioritize) {
        poQueueRef.current.unshift(poName);
      } else {
        poQueueRef.current.push(poName);
      }

      updatePOFetchState(poName, {
        status: "queued",
        error: null,
      });

      processQueue();
    },
    [processQueue, updatePOFetchState],
  );

  return (
    <div className="space-y-4">
      <VerificationRules />

      {Object.keys(groupedByPO).map((poName) => (
        <POCard
          key={poName}
          poName={poName}
          invoiceItems={groupedByPO[poName].items}
          totalPrice={groupedByPO[poName].total}
          invoiceType={systemType}
          fetchState={poFetchMap[poName]}
          onRefresh={() => enqueuePOFetch(poName, true)}
        />
      ))}
    </div>
  );
};
